import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Hero from "@/sections/Hero";
import LiveActivityStrip from "@/sections/LiveActivityStrip";
import LiveNowStrip from "@/sections/LiveNowStrip";
import VideoShowcase from "@/sections/VideoShowcase";
import HowItWorks from "@/sections/HowItWorks";
import SafetyPanel from "@/sections/SafetyPanel";
import TwoWays from "@/sections/TwoWays";
import AboutTeaser from "@/sections/AboutTeaser";
import FounderNote from "@/sections/FounderNote";
import BetaScreensMarquee from "@/sections/BetaScreensMarquee";
import BetaTonightTicker from "@/sections/BetaTonightTicker";
import FoundingVenueWall from "@/sections/FoundingVenueWall";
import PartnerNumbers from "@/sections/PartnerNumbers";
import RequestCity from "@/sections/RequestCity";
import FAQ from "@/sections/FAQ";
import LazySection from "@/sections/LazySection";
import { useSEO } from "@/hooks/useSEO";

export default function Home() {
  useSEO({
    title: "GateMate — Step In. Link Up. | Live Chatrooms for Events & Venues",
    description:
      "A live chatroom for everyone at the same venue or event. Check in, see who's there, and connect with the people sharing your night. Free for attendees.",
    path: "/",
    image: "https://gatemate.net/generated/og-image-v2.jpg",
  });

  return (
    <main data-testid="home-page">
      <Hero />
      <LiveActivityStrip />
      <LiveNowStrip />
      <VideoShowcase />
      <HowItWorks />

      <div className="container-x -mt-6 mb-24 flex justify-center">
        <Link
          to="/how-it-works"
          data-testid="home-how-it-works-link"
          className="inline-flex items-center gap-2 rounded-full glass hover:glass-cyan px-6 py-3 text-sm font-medium text-white transition-all"
        >
          See the full walkthrough <ArrowRight className="w-4 h-4 text-neon-cyan" />
        </Link>
      </div>

      <SafetyPanel />
      <TwoWays />
      <FoundingVenueWall />
      <LazySection minHeight={420}>
        <PartnerNumbers />
      </LazySection>
      <LazySection minHeight={420}>
        <AboutTeaser />
      </LazySection>
      <LazySection minHeight={420}>
        <FounderNote />
      </LazySection>
      <LazySection minHeight={560}>
        <BetaScreensMarquee />
      </LazySection>
      <LazySection minHeight={140}>
        <BetaTonightTicker />
      </LazySection>
      <LazySection minHeight={420}>
        <RequestCity />
      </LazySection>
      <LazySection minHeight={420}>
        <FAQ />
      </LazySection>
      {/* GateMate for Business strip — sits above the footer, never in the
          hero, and carries no pricing (Oct 2026 brief). */}
      <section className="relative py-14" data-testid="home-business-strip">
        <div className="container-x">
          <div className="glass glass-cyan rounded-3xl px-8 py-8 md:px-12 flex flex-col md:flex-row md:items-center gap-5">
            <p className="flex-1 text-base md:text-lg text-zinc-300">
              Running a venue or an event?{" "}
              <span className="text-white font-semibold">See GateMate for Business</span>
            </p>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 shrink-0">
              <Link
                to="/venues"
                data-testid="home-business-venues-link"
                className="group inline-flex items-center gap-1.5 text-base font-semibold text-neon-cyan hover:text-white transition-colors"
              >
                For venues
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/events"
                data-testid="home-business-events-link"
                className="group inline-flex items-center gap-1.5 text-base font-semibold text-neon-cyan hover:text-white transition-colors"
              >
                For events
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
